/**
 * Route ของ Desktop shell ที่ถูก gate ด้วยสิทธิ์จริง
 *
 * เหตุผลที่ต้องมี registry นี้: การซ่อนเมนูอย่างเดียวไม่พอ ถ้า route ยังเปิดอยู่
 * ผู้ใช้ที่พิมพ์ URL ตรง ๆ ก็ยังเข้าถึงหน้าจอได้ทั้งที่ไม่มีสิทธิ์ ทุกรายการเมนูที่
 * ประกาศ `moduleCode` จึงต้องมี route คู่กันหนึ่งเส้นที่ผ่าน guard เดียวกัน
 * (`navigation.test.ts` ยืนยันว่าสองชุดนี้ตรงกันเป๊ะ ไม่มีรายการไหนหลุด)
 *
 * ⚠️ ไฟล์นี้ **ไม่ได้ตัดสินว่ารายการไหนกดได้จากเมนู** — นั่นคือ `availability` ใน
 * `navigation.ts` และรายการที่ยัง `planned`/`phase-2` ยังคงเป็นปุ่ม disabled ไม่มี
 * ลิงก์ให้กด (`docs/SCOPE.md` — ห้ามทำให้หน้าจอที่ยังไม่มี endpoint จริงดูเหมือน
 * ธุรกรรมที่ใช้งานได้) สิ่งที่แผนที่นี้ประกาศคือ "URL นี้มีอยู่และต้องผ่าน guard"
 *
 * รายการที่มีหน้าจอจริงแล้วถูกผูกกับหน้าจอนั้นใน `DesktopRoutePages.tsx` ส่วนที่
 * เหลือจบลงที่ "ไม่มีสิทธิ์" หรือ "มีสิทธิ์แต่หน้าจอนี้ยังไม่เปิดใช้งานในบิลด์นี้"
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย — backend เป็นผู้ปฏิเสธ request จริงเสมอ
 */

import {
  NAV_GROUPS,
  type NavAvailability,
  type NavGroup,
  type NavItem,
} from './navigation';
import type { PermissionAction } from '../lib/permissions/types';

/**
 * เปลี่ยนรหัสผ่านของตัวเอง — route ของ Desktop shell ที่ **ไม่ได้ผูกกับโมดูลธุรกิจ**
 *
 * จึงไม่อยู่ใน `DESKTOP_MODULE_ROUTES` และไม่มี `moduleCode`/`action`: ทุกคนที่
 * เข้าสู่ระบบได้ย่อมมีรหัสผ่านของตัวเองให้เปลี่ยน การใส่ `can()` ให้มันจะสร้างสถานะ
 * "เข้าระบบได้แต่เปลี่ยนรหัสผ่านของตัวเองไม่ได้" ซึ่งไม่มีอยู่ใน RBAC matrix
 *
 * ด่านของมันคือด่านเดียวกับหน้าจอธุรกิจทุกตัว คือ **ต้องมี token** — `App` ลงทะเบียน
 * route นี้ไว้ใต้ layout route เดียวกัน ผู้ที่ยังไม่เข้าสู่ระบบแล้วพิมพ์ URL นี้จึงเจอ
 * หน้าเข้าสู่ระบบเหมือนกับที่พิมพ์ URL ของหน้าจอธุรกิจ
 */
export const ACCOUNT_PASSWORD_PATH = '/account/password';

/** breadcrumb ของ route ข้างบน — ไม่ได้มาจากเมนู จึงต้องระบุเอง */
export const ACCOUNT_GROUP_TITLE = 'บัญชีของฉัน';
export const ACCOUNT_PASSWORD_LABEL = 'เปลี่ยนรหัสผ่าน';

/**
 * "กลับไปหน้าจอเดิม" — ผู้ที่พาไปหน้าเปลี่ยนรหัสผ่านเป็นคนบอกว่าเดิมคือที่ไหน
 *
 * เก็บไว้ใน location state แทนการเดา `navigate(-1)` เพราะผู้ใช้เข้าถึง URL นี้ได้
 * โดยไม่มีประวัติก่อนหน้า (พิมพ์เอง/บุ๊กมาร์ก) ซึ่ง `navigate(-1)` จะไม่พาไปไหนเลย
 * และผู้ใช้จะค้างอยู่บนฟอร์มที่กดปิดไม่ได้ ไม่มี state = กลับหน้าแรก
 */
export interface AccountPasswordLocationState {
  readonly from: string;
}

/**
 * path ของสองหน้าจอที่ลิงก์หากันข้ามโมดูล — ประกาศไว้ที่นี่จุดเดียว
 *
 * `ProductDetailView` พาไปที่ BOM/Routing และ `BomRoutingScreen` พากลับมาที่
 * รายละเอียดสินค้า ถ้าแต่ละฝั่งพิมพ์ path เอง วันที่ path เปลี่ยนจะเหลือลิงก์ที่
 * พาไปหน้าว่างโดยไม่มีเทสต์ไหนเห็น — ค่าทั้งสองถูกใช้ใน `NAV_ITEM_ROUTE_PATHS`
 * ด้านล่างด้วย จึงไม่มีวันเลื่อนออกจาก route จริง
 */
export const PRODUCT_MASTER_PATH = '/master-data/products';
export const BOM_ROUTING_PATH = '/master-data/bom-routing';

/**
 * ทะเบียนผู้ขาย — `WO-P2-M1-002` (P2-1b, D-083)
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับสองค่าข้างบน: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * อ่านค่าเดียวกันนี้ จึงไม่มีวันเลื่อนออกจากกัน
 *
 * `/master-data/suppliers` ไม่ใช่ `/procurement/...` โดยตั้งใจ — สิ่งที่หน้าจอนี้ดูแล
 * คือ **ทะเบียน** ผู้ขาย ซึ่งเป็นข้อมูลหลัก ส่วนใบสั่งซื้อที่อ้างทะเบียนนี้เป็นงานของ
 * P2-2 และจะมี path กลุ่มของมันเอง
 */
export const SUPPLIER_MASTER_PATH = '/master-data/suppliers';

/**
 * ทะเบียนลูกค้า — `WO-P2-M4-003` (P2-4a, D-089)
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * อ่านค่าเดียวกันนี้ จึงไม่มีวันเลื่อนออกจากกัน
 *
 * `/master-data/customers` — เส้นข้าง ๆ ทะเบียนผู้ขาย ไม่ใช่ `/sales/...` โดยตั้งใจ
 * ด้วยเหตุผลเดียวกับที่ผู้ขายไม่ได้อยู่ใต้ `/procurement/`: สิ่งที่หน้าจอนี้ดูแลคือ
 * **ทะเบียน** ลูกค้า ซึ่งเป็นข้อมูลหลักระดับองค์กร ส่วนใบสั่งขายที่อ้างทะเบียนนี้
 * เป็นงานของ P2-4b และจะมี path กลุ่มของมันเอง
 */
export const CUSTOMER_MASTER_PATH = '/master-data/customers';

/**
 * ใบขอซื้อ — `WO-P2-M2-003` (P2-2a, D-084)
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าข้างบน: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * ขึ้นต้นด้วย `/procurement/` ซึ่งเป็น prefix **ใหม่** ของกลุ่มงานจัดซื้อ — ตรงกับ
 * สิ่งที่หมายเหตุของ `SUPPLIER_MASTER_PATH` ข้างบนเขียนล่วงหน้าไว้ว่า "ใบสั่งซื้อที่
 * อ้างทะเบียนนี้เป็นงานของ P2-2 และจะมี path กลุ่มของมันเอง" ทะเบียนผู้ขายยังคงอยู่
 * ใต้ `/master-data/` ตามเดิม ไม่ได้ย้ายตามมา
 */
export const PURCHASE_REQUISITIONS_PATH = '/procurement/purchase-requisitions';

/**
 * ใบสั่งซื้อ — `WO-P2-M2-004` (P2-2b, D-085)
 *
 * เส้นที่สองใต้ prefix `/procurement/` ซึ่งเป็นกลุ่มของงานจัดซื้อ — และเป็นเส้นที่
 * ปิด flow: ใบสั่งซื้อรวมใบขอซื้อที่อนุมัติแล้วจากเส้นข้างบนเข้าเป็นเอกสารเดียว
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 */
export const PURCHASE_ORDERS_PATH = '/procurement/purchase-orders';

/**
 * ข้อยกเว้นการรับของ — `WO-P2-M14-011` (D-168 B6) — **รายการที่สามของกลุ่มเมนู
 * "จัดซื้อ"**
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * อยู่ใต้ `/procurement/` เดียวกับใบขอซื้อ/ใบสั่งซื้อ ไม่ใช่ prefix ใหม่: คิวนี้คือ
 * สิ่งที่จัดซื้อทำงานต่อจากใบสั่งซื้อที่ของมาไม่ครบ — gate ด้วย `PROCUREMENT` + VIEW
 * เดียวกัน ส่วนปุ่ม "อนุมัติ" ข้างในหน้าจอต้องการ `INVENTORY` + APPROVE ซึ่งเป็น grant
 * เดิมของเส้น `POST /v1/receiving-discrepancies/{id}/approve` (D-091) คนละโมดูลกับ
 * ที่เปิดหน้าจอนี้โดยเจตนา
 */
export const RECEIVING_EXCEPTIONS_PATH = '/procurement/receiving-exceptions';

/**
 * ใบสั่งขาย — `WO-P2-M4-004` (P2-4b, D-090)
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * ขึ้นต้นด้วย `/sales/` ซึ่งเป็น prefix **ใหม่** ของกลุ่มงานขาย — ตรงกับสิ่งที่
 * หมายเหตุของ `CUSTOMER_MASTER_PATH` ข้างบนเขียนล่วงหน้าไว้ว่า "ใบสั่งขายที่อ้าง
 * ทะเบียนนี้เป็นงานของ P2-4b และจะมี path กลุ่มของมันเอง" ทะเบียนลูกค้ายังคงอยู่ใต้
 * `/master-data/` ตามเดิม ไม่ได้ย้ายตามมา ด้วยเหตุผลเดียวกับที่ทะเบียนผู้ขายไม่ได้
 * ย้ายไปอยู่ใต้ `/procurement/` ตอนกลุ่มจัดซื้อเกิดขึ้น
 */
export const SALES_ORDERS_PATH = '/sales/sales-orders';

/**
 * Packing — `WO-P2-M9-006` (P2-9f, D-105) — **รายการที่สองของกลุ่มเมนู "ขาย"**
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * อยู่ใต้ `/sales/` เดียวกับใบสั่งขาย ไม่ใช่ prefix ใหม่: Packing เป็นครึ่งแรกของ
 * งานส่งของ (P2-9) ที่แผนกขายเดียวกันเป็นเจ้าของ และ gate ด้วยโมดูล `SALES`
 * เดียวกัน — ครึ่งหลัง (Delivery/Dispatch/Reversal) เป็นงานของ `WO-P2-M9-007`
 * ซึ่งจะได้ path ของตัวเองในกลุ่มเดียวกันนี้
 */
export const PACKING_UNITS_PATH = '/sales/packing';

/**
 * Delivery — `WO-P2-M9-007` (P2-9g, D-105) — **รายการที่สามของกลุ่มเมนู "ขาย"**
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * อยู่ใต้ `/sales/` เดียวกับใบสั่งขายและ Packing ไม่ใช่ prefix ใหม่: Delivery เป็น
 * ครึ่งหลังของงานส่งของ (P2-9) ที่แผนกขายเดียวกันเป็นเจ้าของ และ gate ด้วยโมดูล
 * `SALES` เดียวกัน — ครึ่งแรก (Packing) เป็นงานของ `WO-P2-M9-006` ซึ่งได้ path ของ
 * ตัวเองไปแล้ว
 */
export const DELIVERIES_PATH = '/sales/deliveries';

/**
 * Forecast ลูกค้า — `WO-P2-M5-003` (P2-5a, D-093)
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * ขึ้นต้นด้วย `/planning/` ซึ่งเป็น prefix **ใหม่** ของงานวางแผน — ไม่ใช่ `/sales/`
 * แม้ Forecast จะมาจากลูกค้าเหมือน Customer PO: D-093 ให้ `PLANNING` เป็นโมดูลสิทธิ์
 * ของตัวเอง เพราะ Forecast/MPS/MRP เป็นงานของฝ่ายวางแผนซึ่งไม่ใช่ทั้งการขายและการซื้อ
 * prefix นี้จะถูกใช้ต่อโดย Net Forecast (P2-5b), MPS (P2-5c) และ MRP (P2-6)
 */
export const FORECAST_IMPORTS_PATH = '/planning/forecast-imports';

/**
 * รัน MRP — `WO-P2-M6-006` (P2-6e, D-100/D-101)
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * อยู่ใต้ `/planning/` เดียวกับ Forecast — ไม่ใช่ prefix ใหม่: MRP เป็นปลายทางสุดท้าย
 * ของสาย Forecast → MPS → MRP ที่ฝ่ายวางแผนเดียวกันเป็นเจ้าของ (D-093) และ gate ด้วย
 * โมดูล `PLANNING` เดียวกัน
 */
export const MRP_RUNS_PATH = '/planning/mrp-runs';

/** แผนการผลิต — `WO-P2-M5-006` (D-127) อยู่ใต้ `/planning/` เดียวกับ Forecast/MRP */
export const PRODUCTION_PLAN_PATH = '/planning/production-plan';

/**
 * วางแผนผลิตแบบ Manual — `WO-P2-M5-010` (D-129) — คนละหน้ากับแผนการผลิตข้างบน
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ อยู่ใต้ `/planning/` เดียวกับ Forecast/MRP/แผนการผลิต
 */
export const MANUAL_PLAN_PATH = '/planning/manual-plan';

/**
 * กฎตรวจรับเข้า — `WO-P2-M3-003` (P2-3a, D-086)
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * อยู่ใต้ `/inventory/` ข้าง ๆ การรับเข้าสต๊อก **ไม่ใช่ `/master-data/`** แม้จะเป็น
 * ตารางตั้งค่า: สิ่งที่มันควบคุมคือสถานะของสต๊อกที่กำลังรับเข้า และมันถูก gate ด้วย
 * โมดูล `INVENTORY` เดียวกับงานรับเข้า ไม่ใช่โมดูลข้อมูลหลัก
 */
export const INCOMING_INSPECTION_RULES_PATH = '/inventory/incoming-inspection-rules';

/**
 * เคส NCR — `WO-P2-M11-008` (P2-11b, D-108 ข้อ 2 และ 3)
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * อยู่ใต้ `/inventory/` ข้าง ๆ กฎตรวจรับเข้า ด้วยเหตุผลเดียวกับที่กฎตรวจรับเข้าไม่ได้
 * อยู่ใต้ `/master-data/`: มันถูก gate ด้วยโมดูล `INVENTORY` เดียวกับงานรับเข้า
 * ไม่ใช่โมดูลข้อมูลหลัก
 */
export const NCR_CASES_PATH = '/inventory/ncr-cases';

/**
 * Tool/Die Master — `WO-P2-M11-010` (P2-11d, D-108 ข้อ 4 และ 5) — หน้าจอสุดท้าย
 * ของ Phase 2
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * อยู่ใต้ `/master-data/` ข้าง ๆ BOM/Routing ไม่ใช่ `/inventory/` หรือ
 * `/production/`: `router.py` ของ `WO-P2-M11-009` gate ทุกเส้นด้วย `PRODUCT_MASTER`
 * เดิม ไม่ใช่โมดูลใหม่ — ดูแลเครื่องมือเป็นงานของคนกลุ่มเดียวกับที่ดูแล BOM/Routing
 */
export const TOOL_REGISTRY_PATH = '/master-data/tooling';

/**
 * ใบสั่งผลิต — ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับสองค่าข้างบน
 *
 * หน้าจอของมันเปิดใช้งานแล้ว (`WO-P1-M10-014`) และรายการเมนูชี้ path เดียวกันนี้
 */
export const PRODUCTION_ORDERS_PATH = '/production/orders';

/**
 * ป้าย Kanban การผลิต — `WO-P2-M16-001` (D-169, D-170)
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * อยู่ใต้ `/production/` ข้าง ๆ ใบสั่งผลิต ไม่ใช่ prefix ใหม่: ป้ายเป็นเอกสารของ
 * ใบสั่งผลิตที่ปล่อยแล้ว gate ด้วยโมดูล `PRODUCTION_ORDER` เดียวกัน
 */
export const OUTPUT_TAGS_PATH = '/production/output-tags';

/**
 * ตามสอบ Lot — ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าข้างบน (`WO-P1-M10-020`)
 *
 * เส้นเดียวสำหรับทั้งสองทิศทาง เพราะมันเป็นหน้าจอเดียวที่แสดง `backward` และ
 * `forward` ของ lot เดียวกันพร้อมกัน ส่วน lot ที่กำลังดูอยู่เดินทางมาใน query
 * (`?lot_id=…`) ไม่ใช่ใน path — `App.tsx` ประกาศ route ของทุกโมดูลจาก
 * `DESKTOP_MODULE_ROUTES` แบบ path ตรงตัว จึงไม่มีที่ให้ dynamic segment
 */
export const LOT_TRACEABILITY_PATH = '/traceability/lots';

/**
 * คิวข้อขัดแย้งจากการซิงก์เครื่องสแกน — `WO-P1-M10-021` (D-077)
 *
 * อยู่ใต้ `/production` เพราะมันคือ **คำสั่งหน้างาน** ที่เซิร์ฟเวอร์ปฏิเสธ ไม่ใช่งาน
 * ตั้งค่าระบบ และถูก gate ด้วย `SHOP_FLOOR` เหมือนการสแกนที่ออกคำสั่งนั้นมา
 *
 * ⚠️ ห้ามขึ้นต้นด้วย `/scanner` ซึ่งเป็น shell ของเครื่องสแกน (D-037) — นี่เป็น
 * หน้าจอ Desktop สำหรับคนที่นั่งตัดสิน ไม่ใช่หน้าจอของคนที่ยืนอยู่หน้าเครื่องจักร
 */
export const OFFLINE_CONFLICTS_PATH = '/production/offline-conflicts';

/**
 * Executive Dashboard — `WO-P2-M10-005` (P2-10d, D-106)
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * ขึ้นต้นด้วย `/reporting/` ซึ่งเป็น prefix **ใหม่**: `router.py` ฝั่งเซิร์ฟเวอร์
 * เขียนไว้ตรง ๆ ว่า reporting เป็นโดเมนใหม่ที่มีโมดูลสิทธิ์ของตัวเอง
 * (`EXECUTIVE_DASHBOARD`) ไม่ใช่การเพิ่ม action ให้โมดูลที่มีอยู่แล้ว — นี่คือ
 * โดเมนแรกที่ทำแบบนั้นนับตั้งแต่ `planning` (D-093)
 */
export const EXECUTIVE_DASHBOARD_PATH = '/reporting/executive-dashboard';

/**
 * จ้างผลิตภายนอก (Outsourcing) — `WO-P2-M8-007` (P2-8g, D-103) — **กลุ่มเมนูใหม่**
 * ตั้งค่า Kanban (Replenishment Policy) — `WO-P2-M7-006` (P2-7f, D-102)
 *
 * ประกาศไว้ที่นี่ด้วยเหตุผลเดียวกับค่าอื่น ๆ: รายการเมนูและ `NAV_ITEM_ROUTE_PATHS`
 * ต้องชี้เส้นเดียวกันเสมอ
 *
 * ขึ้นต้นด้วย `/outsourcing/` ซึ่งเป็น prefix **ใหม่**: `schemas.py` ฝั่งเซิร์ฟเวอร์
 * เขียนไว้ตรง ๆ ว่า `OUTSOURCING` เป็นโมดูลสิทธิ์ของตัวเอง ไม่ใช่ `PROCUREMENT`
 * หรือ `SHOP_FLOOR` ที่ใช้ซ้ำ ("sending work out to a vendor is neither buying a
 * part nor making one") — โดเมนที่สามที่ทำแบบนั้นต่อจาก `planning` (D-093) และ
 * `reporting` (D-106)
 */
export const OUTSOURCE_ORDERS_PATH = '/outsourcing/orders';

/**
 * ตั้งค่า Kanban (Replenishment Policy) — `WO-P2-M7-006` (P2-7f, D-102)
 *
 * อยู่ใต้ `/production/kanban/` ซึ่งเป็น prefix **ใหม่**: D-102 แยกสองโมดูลสิทธิ์
 * (`KANBAN_CONFIG`/`KANBAN_EXECUTION`) เพราะ RBAC เป็น `module × action` และ "ตั้ง
 * policy" กับ "สแกนเบิก" ต่างก็เป็น `CREATE` แยกกันด้วย action ไม่ได้ — สอง path
 * ใต้ prefix เดียวกันนี้จึงต้องชี้คนละหน้าจอ คนละโมดูล ตรงตามการแยกนั้น
 */
export const KANBAN_REPLENISHMENT_POLICIES_PATH = '/production/kanban/replenishment-policies';

/**
 * บอร์ด Kanban (Signal) — `WO-P2-M7-006` (P2-7f, D-102) — **คนละหน้าจอ คนละโมดูล**
 * จาก path ข้างบน แม้จะอยู่ใต้ prefix เดียวกัน (`KANBAN_EXECUTION` ไม่ใช่
 * `KANBAN_CONFIG`) — ดูหมายเหตุยาวที่ `KANBAN_REPLENISHMENT_POLICIES_PATH`
 */
export const KANBAN_SIGNALS_PATH = '/production/kanban/signals';

export interface DesktopModuleRoute {
  /** path ของ Desktop shell — ต้องไม่ชนกับ `/scanner` ซึ่งเป็นคนละ shell (D-037) */
  readonly path: string;
  readonly navItemId: string;
  readonly groupTitle: string;
  readonly label: string;
  readonly moduleCode: string;
  readonly action: PermissionAction;
  readonly availability: NavAvailability;
  readonly note?: string;
  /** แม่ของเมนู — มีเฉพาะลูกที่ path ต่างจากแม่ (D-137) ใช้แทรก breadcrumb */
  readonly parent?: { readonly label: string; readonly path: string };
}

/**
 * path ต่อรายการเมนู — เก็บแยกจาก `NavItem.to` โดยเจตนา
 *
 * `NavItem.to` แปลว่า "มีลิงก์ให้กดในเมนู" ซึ่งสงวนไว้ให้รายการที่ `available`
 * เท่านั้น ส่วนแผนที่นี้แปลว่า "URL นี้มีอยู่และต้องผ่าน guard" การใช้ field เดียวกัน
 * ทั้งสองความหมายจะทำให้รายการที่ยังไม่พร้อมกลายเป็นลิงก์กดได้โดยไม่ตั้งใจ
 */
const NAV_ITEM_ROUTE_PATHS: Readonly<Record<string, string>> = {
  'product-master': PRODUCT_MASTER_PATH,
  'bom-routing': BOM_ROUTING_PATH,
  'supplier-master': SUPPLIER_MASTER_PATH,
  'customer-master': CUSTOMER_MASTER_PATH,
  'purchase-requisitions': PURCHASE_REQUISITIONS_PATH,
  'purchase-orders': PURCHASE_ORDERS_PATH,
  'receiving-exceptions': RECEIVING_EXCEPTIONS_PATH,
  'sales-orders': SALES_ORDERS_PATH,
  'packing-units': PACKING_UNITS_PATH,
  deliveries: DELIVERIES_PATH,
  'forecast-imports': FORECAST_IMPORTS_PATH,
  'mrp-runs': MRP_RUNS_PATH,
  'production-plan': PRODUCTION_PLAN_PATH,
  'manual-plan': MANUAL_PLAN_PATH,
  'goods-receipt': '/inventory/goods-receipt',
  'incoming-inspection-rules': INCOMING_INSPECTION_RULES_PATH,
  'ncr-cases': NCR_CASES_PATH,
  'tool-registry': TOOL_REGISTRY_PATH,
  'initial-stock-import': '/inventory/initial-stock-import',
  'inventory-ledger': '/inventory/ledger',
  'product-staging': '/inventory/staging',
  'production-orders': PRODUCTION_ORDERS_PATH,
  'output-tags': OUTPUT_TAGS_PATH,
  'shop-floor-scan': '/production/scan',
  'offline-sync-conflicts': OFFLINE_CONFLICTS_PATH,
  'kanban-replenishment-policy': KANBAN_REPLENISHMENT_POLICIES_PATH,
  'kanban-signals': KANBAN_SIGNALS_PATH,
  'lot-traceability': LOT_TRACEABILITY_PATH,
  'genealogy-backward': '/traceability/backward',
  'genealogy-forward': '/traceability/forward',
  'accounting-documents': '/accounting/documents',
  'order-variance': '/accounting/variance',
  // `WO-P2-UI-001` — แผนกกับพนักงานเป็นคนละหน้าและคนละ URL แล้ว
  // `/system/departments` **ยังเป็นเส้นเดิมของแผนก** ไม่ได้ย้ายไปไหน
  'admin-departments': '/system/departments',
  'admin-employees': '/system/employees',
  'admin-permissions': '/system/permissions',
  'admin-roles': '/system/roles',
  'admin-audit-log': '/system/audit-log',
  'admin-sso-clients': '/system/sso-clients',
  'admin-sso-app-roles': '/system/sso-app-roles',
  'admin-plants': '/system/plants',
  'executive-dashboard': EXECUTIVE_DASHBOARD_PATH,
  'outsource-orders': OUTSOURCE_ORDERS_PATH,
};

function toRoute(group: NavGroup, item: NavItem): DesktopModuleRoute | null {
  const path = NAV_ITEM_ROUTE_PATHS[item.id];
  if (path === undefined || item.moduleCode === undefined || item.action === undefined) {
    return null;
  }
  return {
    path,
    navItemId: item.id,
    groupTitle: group.title,
    label: item.label,
    moduleCode: item.moduleCode,
    action: item.action,
    availability: item.availability,
    ...(item.note === undefined ? {} : { note: item.note }),
  };
}

/**
 * ลูก (`children`) ที่มี path ของตัวเอง (ต่างจากแม่) ต้องมี route guard ของตัวเอง
 * ด้วย (D-130, `manual-plan` ใต้ `production-plan`) — ลูกที่ path เดียวกับแม่
 * (เช่น `sales-orders-receive`) ไม่มีคู่ใน `NAV_ITEM_ROUTE_PATHS` จึงยังถูก `toRoute`
 * กรองออกเหมือนเดิมทุกประการ ไม่ต้องแยกเงื่อนไข
 *
 * ลูกที่ path ต่างจากแม่ได้ `parent` ติดไปด้วย (D-137) เพื่อให้ `DesktopShell`
 * แทรก breadcrumb ของแม่ได้โดยไม่ต้องเดาจาก URL
 */
export const DESKTOP_MODULE_ROUTES: readonly DesktopModuleRoute[] = NAV_GROUPS.flatMap((group) =>
  group.items.flatMap((item) => {
    const ownRoute = toRoute(group, item);
    const parentPath = NAV_ITEM_ROUTE_PATHS[item.id];
    const childRoutes = (item.children ?? [])
      .map((child) => toRoute(group, child))
      .filter((route): route is DesktopModuleRoute => route !== null)
      .map((route) =>
        parentPath !== undefined && parentPath !== route.path
          ? { ...route, parent: { label: item.label, path: parentPath } }
          : route,
      );
    return ownRoute === null ? childRoutes : [ownRoute, ...childRoutes];
  }),
);

export function findModuleRoute(pathname: string): DesktopModuleRoute | undefined {
  return DESKTOP_MODULE_ROUTES.find((route) => route.path === pathname);
}
